import React, { useMemo } from 'react';
import { css } from '@linaria/core';
import forwardRefAs from 'forward-ref-as';
import { theme, brand, findColorFromCSSVariable } from '@terveystalo/design-system-theme';
import { classnames } from '../../utils';
import { ButtonVariant } from './types';
import { Icon } from '../Icon';
import { focusBoxShadow, focusVisible } from '../../css-utils/focus-visible';
import { calcPadding3_5 } from '../../css-utils/calculation';
import { isIconRightDirectional } from '../../utils/icons';
import { usePreventClick } from '../../utils/usePreventClick';
import { useColorFilter } from '../../css-utils/use-color-filter';
/**
 * Common styles for rounded buttons
 *
 * Used for Button and PillButton
 */
export const sharedButtonStyles = css `
  position: relative;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${brand.button.border.default.radius};

  /* Remove tap highlight in mobile context for some browsers */
  -webkit-tap-highlight-color: transparent;

  /* This will make the button texts a bit more readable as the characters are
  clearly separated from each other */
  font-variant-ligatures: no-common-ligatures;

  text-decoration: none;
  text-align: center;
  transform: perspective(1px) translateZ(0);

  &[aria-disabled='true'] {
    cursor: default;
  }

  &::before {
    content: '';
    border-radius: ${brand.button.border.default.radius};
    position: absolute;
    z-index: -1;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    transform: scaleX(0);
    transform-origin: 50%;
    transition: transform ${theme.animation.duration.default} ease-in-out;
  }

  > img {
    flex-shrink: 0;
  }

  /* On-press (hover, active) styles */
  &:not([aria-disabled='true']) {
    cursor: pointer;

    &:hover,
    &:active {
      &::before {
        transform: scaleX(1);
      }
    }
  }
`;
const wrapperStyles = css `
  padding: ${calcPadding3_5(theme.typography.lineHeight.button.m, '1', 'xs')};
  border: ${theme.border.width.xs} solid;

  font-family: ${brand.button.font.cta.family};
  font-weight: ${brand.button.font.cta.weight};
  letter-spacing: ${brand.button.font.cta.letterSpacing};
  font-size: ${brand.button.font.cta.size};
  line-height: ${brand.button.font.cta.lineHeight};

  /* Icon styles */
  > img {
    /* Force the icon over the container to make it work with the content */
    margin-left: -${theme.spacing['1_5']};
    transition: transform ${theme.animation.duration.default};
  }
`;
const contentStyles = css `
  display: flex;
  padding: 0 ${theme.spacing['2_5']};
  width: 100%;
  justify-content: center;
  flex-shrink: 0;
`;
/**
 * Configuration of the button styles for the different
 * variants
 */
const variantStyles = {
    [ButtonVariant.primary]: [
        css `
      background-color: ${brand.button.color.primary.positive.default.background};
      color: ${brand.button.color.primary.positive.default.content};
      border-color: ${brand.button.color.primary.positive.default.border};

      &::before {
        background-color: ${brand.button.color.primary.positive.hover.background};
      }

      &[aria-disabled='true'] {
        border-color: ${brand.button.color.primary.positive.disabled.border};
        background-color: ${brand.button.color.primary.positive.disabled.background};
        color: ${brand.button.color.primary.positive.disabled.content};
      }

      &:not([aria-disabled='true']) {
        &:hover,
        &:active {
          color: ${brand.button.color.primary.positive.hover.content};
          border-color: ${brand.button.color.primary.positive.hover.border};
        }
      }

      ${focusVisible(`
            outline: 0;
            box-shadow: 0 0 0 ${theme.border.width.xs} ${brand.button.color.primary.positive.focus.outline.inner}, 0 0 0 ${theme.border.width.s} ${brand.button.color.primary.positive.focus.outline.outer};
        `, `
          box-shadow: none;
        `)}
    `,
    ],
    [ButtonVariant.secondary]: [
        css `
      color: ${brand.button.color.secondary.positive.default.content};
      background-color: ${brand.button.color.secondary.positive.default.background};
      border-color: ${brand.button.color.secondary.positive.default.border};

      &::before {
        background-color: ${brand.button.color.secondary.positive.hover.background};
      }

      &[aria-disabled='true'] {
        border-color: ${brand.button.color.secondary.positive.disabled.border};
        background-color: ${brand.button.color.secondary.positive.disabled.background};
        color: ${brand.button.color.secondary.positive.disabled.content};
      }

      &:not([aria-disabled='true']) {
        &:hover,
        &:active {
          border-color: ${brand.button.color.secondary.positive.hover.border};
          color: ${brand.button.color.secondary.positive.hover.content};
          > img {
            filter: var(--icon-filter);
          }
        }
      }
    `,
        focusBoxShadow,
    ],
    [ButtonVariant.primaryNegative]: [
        css `
      color: ${brand.button.color.primary.negative.default.content};
      background-color: ${brand.button.color.primary.negative.default.background};
      border-color: ${brand.button.color.primary.negative.default.border};

      &::before {
        background-color: ${brand.button.color.primary.negative.hover.background};
      }

      &[aria-disabled='true'] {
        color: ${brand.button.color.primary.negative.disabled.content};
      }

      &:not([aria-disabled='true']) {
        &:hover,
        &:active {
          color: ${brand.button.color.primary.negative.hover.content};
          > img {
            filter: var(--icon-filter);
          }
        }
      }

      ${focusVisible(`
          outline: 0;
          box-shadow: 0 0 0 ${theme.border.width.xs} ${brand.button.color.primary.negative.focus.outline.inner}, 0 0 0 ${theme.border.width.s} ${brand.button.color.primary.negative.focus.outline.outer};
        `, `
          box-shadow: none;
        `)}
    `,
    ],
    [ButtonVariant.secondaryNegative]: [
        css `
      color: ${brand.button.color.secondary.negative.default.content};
      background-color: ${brand.button.color.secondary.negative.default.background};
      border-color: ${brand.button.color.secondary.negative.default.border};

      &::before {
        background-color: ${brand.button.color.secondary.negative.hover.background};
      }

      &[aria-disabled='true'] {
        color: ${brand.button.color.secondary.negative.disabled.content};
        border-color: ${brand.button.color.secondary.negative.disabled.border};
      }

      &:not([aria-disabled='true']) {
        &:hover,
        &:active {
          color: ${brand.button.color.secondary.negative.hover.content};
        }
      }

      ${focusVisible(`
          outline: 0;
          box-shadow: 0 0 0 ${theme.border.width.xs}  ${brand.button.color.secondary.negative.focus.outline.inner}, 0 0 0 ${theme.border.width.s}  ${brand.button.color.secondary.negative.focus.outline.outer};
        `, `
          box-shadow: none;
        `)}
    `,
    ],
};
const animateRightStyles = css `
  &:not([aria-disabled='true']) {
    /* Move image to the right on interaction */
    &:active > img {
      transform: translateX(0.25rem);
    }

    @media (hover: hover) {
      &:hover > img {
        transform: translateX(0.25rem);
      }
      &:active > img {
        transform: translateX(0.5rem);
      }
    }
  }
`;
/**
 * Determine the base icon color. This doesn't work for the active/hover states however,
 * those are handled in the styles
 */
const determineIconColor = (variant, disabled) => {
    const state = disabled ? 'disabled' : 'default';
    switch (variant) {
        case ButtonVariant.primary:
            return findColorFromCSSVariable(brand.button.color.primary.positive[state].icon);
        case ButtonVariant.secondaryNegative:
            return findColorFromCSSVariable(brand.button.color.secondary.negative[state].icon);
        case ButtonVariant.secondary:
            return findColorFromCSSVariable(brand.button.color.secondary.positive[state].icon);
        case ButtonVariant.primaryNegative:
            return findColorFromCSSVariable(brand.button.color.primary.negative[state].icon);
        default:
            return 'lake';
    }
};
/**
 * Determine proper svg filter to transform the icons into the correct color in hover state
 * without loading a completely new icon.
 */
const determineIconHoverTargetColor = (variant) => {
    switch (variant) {
        case ButtonVariant.secondary:
            return findColorFromCSSVariable(brand.button.color.secondary.positive.hover.icon);
        case ButtonVariant.primaryNegative:
            return findColorFromCSSVariable(brand.button.color.primary.negative.hover.icon);
        default:
            return;
    }
};
/**
 * Button component which extends the native button element. The native element can be changed with
 * the `as` prop which also provides the types for the specific element attributes/properties.
 *
 * The button variant can be set with `variant` prop.
 */
export const Button = forwardRefAs(({ as: Component = 'button', variant = 'primary', icon, className, children, disabled, onClick, style, ...rest }, ref) => {
    // Apply animation styles for supported icons
    const wrapperIconStyles = isIconRightDirectional(icon) && animateRightStyles;
    const handleClick = usePreventClick(disabled, onClick);
    const color = useMemo(() => determineIconHoverTargetColor(variant), [variant]);
    const { component, filter } = useColorFilter({ color, id: ['button', variant] });
    return (React.createElement(React.Fragment, null,
        component,
        React.createElement(Component, { className: classnames(sharedButtonStyles, wrapperStyles, ...variantStyles[variant], wrapperIconStyles, className), style: {
                ...style,
                '--icon-filter': filter,
            }, ref: ref, "aria-disabled": disabled, onClick: handleClick, ...rest },
            React.createElement("span", { className: classnames(contentStyles) }, children),
            icon && React.createElement(Icon, { name: icon, color: determineIconColor(variant, disabled) }))));
});


import "../../../css/atoms/Button/Button.css";
